import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { 
  Trash2, Plus, Layers, Check, X, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import axios from "axios";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const Outfits = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [outfits, setOutfits] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newOutfitName, setNewOutfitName] = useState("");
  const [newOutfitDescription, setNewOutfitDescription] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [outfitsRes, wishlistRes] = await Promise.all([
        axios.get(`${API}/outfits`, { withCredentials: true }),
        axios.get(`${API}/wishlist`, { withCredentials: true })
      ]);
      setOutfits(outfitsRes.data.outfits || []);
      setWishlistItems(wishlistRes.data.items || []);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const createOutfit = async () => {
    if (!newOutfitName.trim()) {
      toast.error("Please enter an outfit name");
      return;
    }
    if (selectedItems.length < 2) {
      toast.error("Please select at least 2 items");
      return;
    }

    setIsCreating(true);
    try {
      const items = selectedItems.map(id => wishlistItems.find(i => i.item_id === id));
      const colors = [...new Set(items.map(i => i.color_hex).filter(Boolean))];
      
      await axios.post(`${API}/outfits`, {
        name: newOutfitName,
        description: newOutfitDescription,
        items: items.map(i => ({
          item_id: i.item_id,
          title: i.title,
          url: i.url,
          affiliate_url: i.affiliate_url,
          image_url: i.image_url,
          price: i.price,
          source: i.source,
          color_hex: i.color_hex,
          category: i.category
        })),
        colors: colors
      }, { withCredentials: true });
      
      toast.success("Outfit created!");
      setShowCreateModal(false);
      setNewOutfitName("");
      setNewOutfitDescription("");
      setSelectedItems([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to create outfit");
    } finally {
      setIsCreating(false);
    }
  };

  const deleteOutfit = async (outfitId) => {
    setDeletingId(outfitId);
    try {
      await axios.delete(`${API}/outfits/${outfitId}`, { withCredentials: true });
      setOutfits(prev => prev.filter(o => o.outfit_id !== outfitId));
      toast.success("Outfit deleted");
    } catch (error) {
      toast.error("Failed to delete outfit");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const trackClick = async (item) => {
    try {
      await axios.post(`${API}/track-click`, {
        product_url: item.url,
        affiliate_url: item.affiliate_url || item.url,
        source: item.source,
        color_hex: item.color_hex,
        category: item.category
      }, { withCredentials: true });
    } catch (error) {
      console.error("Failed to track click");
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-primary)' }} data-testid="outfits-page">
      <main className="container py-12">
        <div className="max-w-6xl mx-auto">
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-12 animate-fade-in">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Layers className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                <p className="text-meta uppercase tracking-widest" style={{ color: 'var(--accent-primary)' }}>
                  Outfit Combiner
                </p>
              </div>
              <h1 className="text-display mb-4" style={{ color: 'var(--text-primary)' }}>
                Your Outfits
              </h1>
              <p className="text-body text-secondary max-w-lg">
                Combine items from your wishlist into complete outfit collections.
              </p>
            </div>
            <button 
              onClick={() => setShowCreateModal(true)} 
              className="btn-primary"
              data-testid="create-outfit-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Outfit
            </button>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-shimmer w-32 h-1 rounded-full"></div>
            </div>
          ) : outfits.length === 0 ? (
            <div className="text-center py-20 animate-fade-in">
              <div 
                className="w-24 h-24 mx-auto mb-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--surface-secondary)' }}
              >
                <Layers className="w-10 h-10" style={{ color: 'var(--text-muted)' }} />
              </div>
              <h3 className="text-h3 mb-3" style={{ color: 'var(--text-primary)' }}>
                No outfits yet
              </h3>
              <p className="text-body text-secondary mb-8 max-w-sm mx-auto">
                Combine items from your wishlist into complete outfit collections.
              </p>
              <button onClick={() => setShowCreateModal(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Outfit
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
              {outfits.map((outfit, index) => (
                <div 
                  key={outfit.outfit_id} 
                  className="card group relative"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`outfit-card-${index}`}
                >
                  {/* Delete Button */}
                  <button 
                    onClick={() => deleteOutfit(outfit.outfit_id)} 
                    disabled={deletingId === outfit.outfit_id} 
                    className="absolute top-4 right-4 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                    style={{ backgroundColor: 'var(--surface-secondary)' }}
                  >
                    {deletingId === outfit.outfit_id ? (
                      <div className="animate-shimmer w-4 h-4 rounded"></div>
                    ) : (
                      <Trash2 className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                    )}
                  </button>

                  {/* Outfit Info */}
                  <h3 className="text-h3 mb-2" style={{ color: 'var(--text-primary)' }}>{outfit.name}</h3>
                  {outfit.description && (
                    <p className="text-small text-secondary mb-4">{outfit.description}</p>
                  )}
                  
                  {/* Color Swatches */}
                  {outfit.colors?.length > 0 && (
                    <div className="flex gap-2 mb-6">
                      {outfit.colors.slice(0, 5).map((color, i) => (
                        <div 
                          key={i} 
                          className="w-8 h-8 rounded-lg"
                          style={{ backgroundColor: color, border: '1px solid var(--border-subtle)' }}
                        />
                      ))}
                    </div>
                  )}

                  {/* Items Grid */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {outfit.items?.slice(0, 6).map((item, i) => (
                      <a 
                        key={i} 
                        href={item.affiliate_url || item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={() => trackClick(item)}
                        className="aspect-square overflow-hidden rounded-lg group/item relative"
                        style={{ backgroundColor: 'var(--surface-secondary)' }}
                      >
                        {item.image_url && (
                          <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                        )}
                        <div 
                          className="absolute inset-0 opacity-0 group-hover/item:opacity-100 transition-opacity flex items-center justify-center"
                          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                        >
                          <ExternalLink className="w-4 h-4" style={{ color: 'var(--text-inverse)' }} />
                        </div>
                      </a>
                    ))}
                  </div>

                  <p className="text-meta text-muted">{outfit.items?.length || 0} items</p>
                </div>
              ))}
            </div>
          )}

        </div>
      </main>

      {/* Create Outfit Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--background-overlay)' }}
        >
          <div 
            className="card w-full max-w-2xl max-h-[80vh] overflow-hidden animate-fade-in"
            style={{ backgroundColor: 'var(--surface-primary)' }}
          >
            {/* Modal Header */}
            <div 
              className="p-6 flex items-center justify-between"
              style={{ borderBottom: '1px solid var(--border-subtle)' }}
            >
              <h2 className="text-h2" style={{ color: 'var(--text-primary)' }}>Create New Outfit</h2>
              <button 
                onClick={() => setShowCreateModal(false)} 
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4 mb-8">
                <div>
                  <label className="text-meta text-muted uppercase tracking-widest block mb-2">
                    Outfit Name
                  </label>
                  <input 
                    value={newOutfitName} 
                    onChange={(e) => setNewOutfitName(e.target.value)} 
                    placeholder="Summer Casual Look" 
                    className="input"
                  />
                </div>
                <div>
                  <label className="text-meta text-muted uppercase tracking-widest block mb-2">
                    Description (optional)
                  </label>
                  <input 
                    value={newOutfitDescription} 
                    onChange={(e) => setNewOutfitDescription(e.target.value)} 
                    placeholder="Perfect for beach vacations" 
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="text-small mb-4 block" style={{ color: 'var(--text-secondary)' }}>
                  Select Items from Wishlist 
                  <span className="ml-2" style={{ color: 'var(--accent-primary)' }}>
                    ({selectedItems.length} selected)
                  </span>
                </label>
                
                {wishlistItems.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-small text-muted mb-4">No items in wishlist</p>
                    <Link to="/wishlist" className="btn-secondary text-small">
                      Go to Wishlist
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-3">
                    {wishlistItems.map((item) => (
                      <div 
                        key={item.item_id} 
                        onClick={() => toggleItemSelection(item.item_id)} 
                        className="cursor-pointer rounded-lg overflow-hidden relative transition-all"
                        style={{ 
                          opacity: selectedItems.includes(item.item_id) ? 1 : 0.6,
                          border: selectedItems.includes(item.item_id) 
                            ? '2px solid var(--accent-primary)' 
                            : '2px solid transparent'
                        }}
                      >
                        <div className="aspect-square" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                          {item.image_url && (
                            <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                          )}
                        </div>
                        {selectedItems.includes(item.item_id) && (
                          <div 
                            className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: 'var(--accent-primary)' }}
                          >
                            <Check className="w-3 h-3" style={{ color: 'var(--text-primary)' }} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div 
              className="p-6 flex justify-end gap-3"
              style={{ borderTop: '1px solid var(--border-subtle)' }}
            >
              <button 
                onClick={() => setShowCreateModal(false)} 
                className="btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={createOutfit} 
                disabled={isCreating || selectedItems.length < 2} 
                className="btn-primary"
              >
                {isCreating ? (
                  <span className="animate-shimmer w-16 h-4 rounded inline-block"></span>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Outfit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Outfits;
